import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';

// Shared Components
import Login from './components/Login';
import PlatformSelector from './components/PlatformSelector';
import StoreSelector from './components/StoreSelector';
import SkuMapping from './components/sku/SkuMapping';
import { authAPI } from './services/api';
import { setSession, clearSession } from './services/session';

// Shopify Components
import ShopifyDashboard from './components/shopify/Dashboard';
import ShopifyProductList from './components/shopify/ProductList';
import ShopifyOrderList from './components/shopify/OrderList';
import ShopifyReports from './components/shopify/Reports';
import ShopifySettings from './components/shopify/Settings';
import ShopifySidebar from './components/shopify/Sidebar';

// eBay Components
import EbayDashboard from './components/ebay/Dashboard';
import EbayProductList from './components/ebay/ProductList';
import EbayOrderList from './components/ebay/OrderList';
import EbayReports from './components/ebay/Reports';
import EbaySettings from './components/ebay/Settings';
import EbaySidebar from './components/ebay/Sidebar';

// Amazon Components
import AmazonDashboard from './components/amazon/Dashboard';
import AmazonProductList from './components/amazon/ProductList';
import AmazonOrderList from './components/amazon/OrderList';
import AmazonReports from './components/amazon/Reports';
import AmazonSettings from './components/amazon/Settings';
import AmazonSidebar from './components/amazon/Sidebar';

// ✅ Ledger Components
import LedgerDashboard from './components/ledger/Ledgerdashboard';
import LedgerSidebar from './components/ledger/LedgerSidebar';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showSkuMapping, setShowSkuMapping] = useState(false);

  // permissions can be changed by an admin at any time — refresh them whenever the app loads
  const [, setUserVersion] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) return;
    authAPI.me()
      .then(res => { setSession(null, res.data.user); setUserVersion(v => v + 1); })
      .catch(() => { /* a 401 is handled globally (logs out) */ });
  }, [isAuthenticated]);

  const handleLogin = (token, user) => {
    setSession(token, user);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearSession();
    setIsAuthenticated(false);
    setSelectedPlatform(null);
    setSelectedStore(null);
    setShowSkuMapping(false);
  };

  const handleSelectPlatform = (platform) => {
    setSelectedPlatform(platform);
    setShowSkuMapping(false);
    if (platform === 'shopify') {
      setSelectedStore({ id: 1, store_name: 'Shopify Store', platform: 'shopify' });
    }
    // ✅ Ledger needs no store selection
  };

  const handleBackToPlatforms = () => {
    setSelectedPlatform(null);
    setSelectedStore(null);
    setShowSkuMapping(false);
  };

  const handleBackToStores = () => {
    setSelectedStore(null);
    setShowSkuMapping(false);
  };

  const handleSelectStore = (store) => {
    setSelectedStore({ ...store, platform: selectedPlatform });
  };

  if (!isAuthenticated) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  if (!selectedPlatform) {
    return (
      <>
        <PlatformSelector onSelectPlatform={handleSelectPlatform} onLogout={handleLogout} />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  if ((selectedPlatform === 'ebay' || selectedPlatform === 'amazon') && showSkuMapping) {
    return (
      <>
        <div className="min-h-screen bg-gray-100">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center">
            <button
              onClick={() => setShowSkuMapping(false)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Stores
            </button>
          </div>
          <SkuMapping platform={selectedPlatform} />
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  if ((selectedPlatform === 'amazon' || selectedPlatform === 'ebay') && !selectedStore) {
    return (
      <>
        <StoreSelector
          platform={selectedPlatform}
          onBack={handleBackToPlatforms}
          onSelectStore={handleSelectStore}
          onOpenSkuMapping={(selectedPlatform === 'ebay' || selectedPlatform === 'amazon') ? () => setShowSkuMapping(true) : undefined}
        />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  const renderPlatformApp = () => {
    // ✅ LEDGER PLATFORM
    if (selectedPlatform === 'ledger') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <LedgerSidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"          element={<LedgerDashboard activeTab="ledger"    />} />
                <Route path="/transactions" element={<LedgerDashboard activeTab="transactions" />} />
                <Route path="/statement"    element={<LedgerDashboard activeTab="statement"    />} />
                <Route path="/reports"      element={<LedgerDashboard activeTab="reports"      />} />
                <Route path="*"             element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'shopify') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <ShopifySidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<ShopifyDashboard />} />
                <Route path="/products"    element={<ShopifyProductList />} />
                <Route path="/orders"      element={<ShopifyOrderList />} />
                <Route path="/reports"     element={<ShopifyReports />} />
                <Route path="/sku-mapping" element={<SkuMapping platform="shopify" />} />
                <Route path="/settings"    element={<ShopifySettings />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'ebay') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <EbaySidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
              onBackToStores={handleBackToStores}
              storeName={selectedStore?.store_name}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<EbayDashboard    storeId={selectedStore?.id} />} />
                <Route path="/products"    element={<EbayProductList  storeId={selectedStore?.id} />} />
                <Route path="/orders"      element={<EbayOrderList    storeId={selectedStore?.id} />} />
                <Route path="/reports"     element={<EbayReports      storeId={selectedStore?.id} />} />
                <Route path="/sku-mapping" element={<SkuMapping platform="ebay" />} />
                <Route path="/settings"    element={<EbaySettings     storeId={selectedStore?.id} />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'amazon') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <AmazonSidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
              onBackToStores={handleBackToStores}
              storeName={selectedStore?.store_name}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<AmazonDashboard   storeId={selectedStore?.id} />} />
                <Route path="/products"    element={<AmazonProductList storeId={selectedStore?.id} />} />
                <Route path="/orders"      element={<AmazonOrderList   storeId={selectedStore?.id} />} />
                <Route path="/reports"     element={<AmazonReports     storeId={selectedStore?.id} />} />
                <Route path="/sku-mapping" element={<SkuMapping platform="amazon" />} />
                <Route path="/settings"    element={<AmazonSettings    storeId={selectedStore?.id} />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }
  };

  return (
    <>
      {renderPlatformApp()}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;